import { ChartsModule } from 'ng2-charts';

import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { AngularFrameworkLibrary, WidgetsModule } from '@brafton/angular-framework-library';
import {
  BraftonStylesLibraryModule,
  DialogStylesLibarary,
  ElementStylesLibarary,
  MessagesStylesLibarary,
} from '@brafton/angular-styles-library';
import { AssetsModule } from '@brafton/assets-api';
import { CommonFrameworkLibraryModule } from '@brafton/common-framework-library';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';

import { ContentProjectImageComponent } from '../content/components';
import {
  SharedActionAssetDropdownComponent,
  SharedAssetImageComponent,
  SharedAssetImageContentDirective,
  SharedCalendarUnitsHeaderComponent,
  SharedCampaignListingComponent,
  SharedClustedProfileImagesComponent,
  SharedContentListingComponent,
  SharedCreateCampaignButtonComponent,
  SharedCreateContentButtonComponent,
  SharedCreateIdeaButtonComponent,
  SharedCreateOrganizationButtonComponent,
  SharedCreateProductionButtonComponent,
  SharedCreateProjectButtonComponent,
  SharedFilterBarComponent,
  SharedFilterChipComponent,
  SharedFilterPanelCampaignsComponent,
  SharedFilterPanelComponent,
  SharedFilterPanelContentTypeComponent,
  SharedFilterPanelDateRangeComponent,
  SharedFilterPanelDepartmentsComponent,
  SharedFilterPanelIndustrySectorsComponent,
  SharedFilterPanelMonthsComponent,
  SharedFilterPanelOrganizationsComponent,
  SharedFilterPanelOwnersByTeamComponent,
  SharedFilterPanelOwnersComponent,
  SharedFilterPanelPaceOffsetComponent,
  SharedFilterPanelProductClassesComponent,
  SharedFilterPanelProjectStatusComponent,
  SharedFilterPanelSowsComponent,
  SharedFilterPanelSowTypeComponent,
  SharedFilterPanelStatusComponent,
  SharedFilterPanelTagComponent,
  SharedFilterPanelTeamsComponent,
  SharedFilterPanelTemplatesComponent,
  SharedFilterPanelUnitsComponent,
  SharedFilterResultsCountComponent,
  SharedLayeredNumberDescriptionComponent,
  SharedListingActionDropdownComponent,
  SharedListingGroupHeaderComponent,
  SharedMasterProjectImagePanelComponent,
  SharedMasterProjectsImageGridComponent,
  SharedMasterProjectsListingComponent,
  SharedOrganizationListingComponent,
  SharedPagingButtonComponent,
  SharedPendingProjectsListingComponent,
  SharedProfileHeaderComponent,
  SharedSideMenuComponent,
  SharedSimpleSearchInputComponent,
  SharedSowListingComponent,
  SharedSpaceChipComponent,
  SharedSpacesListingComponent,
  SharedTagChipComponent,
  SharedTasksContainerComponent,
  SharedTasksListingCompactComponent,
  SharedTasksListingComponent,
  SharedTypingConversationMessageComponent,
  SharedUnitsListComponent,
  SharedUnitsValueIconsComponent,
  SharedUsersDropdownComponent,
  SharedUsersListingComponent,
  SharedViewOptionChipComponent,
  SharedViewOptionsPanelComponent,
} from './components';
import {
  BfMixedChartDirective,
  SharedHasPermissionDirective,
  SharedHasPermissionsDirective,
  SharedInViewDirective,
  SharedListingColumnWidthDirective,
  SharedResizedDirective,
} from './directives';
import { FindRoutePipe, FindSow, RoundNumberPipe } from './pipes';

const materialImports = [
  ChartsModule,
  DragDropModule,
  FormsModule,
  MatAutocompleteModule,
  MatBadgeModule,
  MatButtonModule,
  MatButtonToggleModule,
  MatCardModule,
  MatCheckboxModule,
  MatChipsModule,
  MatDatepickerModule,
  MatDialogModule,
  MatDividerModule,
  MatExpansionModule,
  MatGridListModule,
  MatIconModule,
  MatInputModule,
  MatListModule,
  MatMenuModule,
  MatNativeDateModule,
  MatPaginatorModule,
  MatProgressBarModule,
  MatProgressSpinnerModule,
  MatRadioModule,
  MatSelectModule,
  MatSidenavModule,
  MatSliderModule,
  MatSlideToggleModule,
  MatSnackBarModule,
  MatSortModule,
  MatStepperModule,
  MatTableModule,
  MatTabsModule,
  MatToolbarModule,
  MatTooltipModule,
  ReactiveFormsModule,
  NgxSliderModule,
  CKEditorModule,
];

const componentsImports = [
  SharedActionAssetDropdownComponent,
  SharedAssetImageComponent,
  SharedAssetImageContentDirective,
  SharedCalendarUnitsHeaderComponent,
  SharedCampaignListingComponent,
  SharedClustedProfileImagesComponent,
  SharedContentListingComponent,
  SharedCreateCampaignButtonComponent,
  SharedCreateContentButtonComponent,
  SharedCreateIdeaButtonComponent,
  SharedCreateOrganizationButtonComponent,
  SharedCreateProductionButtonComponent,
  SharedCreateProjectButtonComponent,
  SharedFilterBarComponent,
  SharedFilterChipComponent,
  SharedFilterPanelCampaignsComponent,
  SharedFilterPanelComponent,
  SharedFilterPanelContentTypeComponent,
  SharedFilterPanelDepartmentsComponent,
  SharedFilterPanelIndustrySectorsComponent,
  SharedFilterPanelDateRangeComponent,
  SharedFilterPanelMonthsComponent,
  SharedFilterPanelOrganizationsComponent,
  SharedFilterPanelOwnersByTeamComponent,
  SharedFilterPanelOwnersComponent,
  SharedFilterPanelPaceOffsetComponent,
  SharedFilterPanelProductClassesComponent,
  SharedFilterPanelProjectStatusComponent,
  SharedFilterPanelSowsComponent,
  SharedFilterPanelSowTypeComponent,
  SharedFilterPanelStatusComponent,
  SharedFilterPanelTagComponent,
  SharedFilterPanelTeamsComponent,
  SharedFilterPanelTemplatesComponent,
  SharedFilterPanelUnitsComponent,
  SharedFilterResultsCountComponent,
  SharedLayeredNumberDescriptionComponent,
  SharedListingActionDropdownComponent,
  SharedListingGroupHeaderComponent,
  SharedMasterProjectImagePanelComponent,
  SharedMasterProjectsImageGridComponent,
  SharedMasterProjectsListingComponent,
  SharedOrganizationListingComponent,
  SharedPagingButtonComponent,
  SharedPendingProjectsListingComponent,
  SharedProfileHeaderComponent,
  SharedSideMenuComponent,
  SharedSimpleSearchInputComponent,
  SharedSowListingComponent,
  SharedSpaceChipComponent,
  SharedSpacesListingComponent,
  SharedTagChipComponent,
  SharedTasksContainerComponent,
  SharedTasksListingCompactComponent,
  SharedTasksListingComponent,
  SharedTypingConversationMessageComponent,
  SharedUnitsListComponent,
  SharedUnitsValueIconsComponent,
  SharedUsersDropdownComponent,
  SharedUsersListingComponent,
  SharedViewOptionChipComponent,
  SharedViewOptionsPanelComponent,
];

const directives = [
  SharedHasPermissionsDirective,
  SharedHasPermissionDirective,
  SharedResizedDirective,
  BfMixedChartDirective,
  SharedListingColumnWidthDirective,
  SharedInViewDirective,
];

const sharedComponentsFromOtherModules: any = [ContentProjectImageComponent];

const pipes: any = [FindRoutePipe, RoundNumberPipe, FindSow];

const libraries = [
  AngularFrameworkLibrary,
  BraftonStylesLibraryModule,
  CommonFrameworkLibraryModule,
  DialogStylesLibarary,
  ElementStylesLibarary,
  MessagesStylesLibarary,
  WidgetsModule,
];

@NgModule({
  declarations: [componentsImports, directives, sharedComponentsFromOtherModules, pipes],
  imports: [AssetsModule, CommonModule, materialImports, RouterModule, libraries],
  exports: [
    AssetsModule,
    componentsImports,
    directives,
    materialImports,
    RouterModule,
    sharedComponentsFromOtherModules,
    libraries,
    pipes,
  ],
  providers: [{ provide: APP_BASE_HREF, useValue: '/' }],
})
export class SharedModule {}
